import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { Buffer } from 'buffer';
import "@/index.css";
import App from "@/App";
import * as serviceWorkerRegistration from "@/utils/serviceWorkerRegistration";
import logger from './utils/logger';

// Make Buffer available globally for third-party libraries (e.g., docx)
window.Buffer = Buffer;

// Initialize Sentry for error tracking (if DSN is configured)
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development',
    release: process.env.REACT_APP_SENTRY_RELEASE || 'acusightpro-frontend@2.0.0',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.2, // 20% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Don't send PII
    beforeSend(event) {
      // Remove sensitive data if needed
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
      }
      return event;
    },
  });
  if (process.env.NODE_ENV === 'development') logger.log('Sentry error tracking initialized');
} else {
  if (process.env.NODE_ENV === 'development') logger.log('ℹ️ Sentry DSN not configured - error tracking disabled');
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">We've been notified and are working on a fix.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      )}
      onError={(error, componentStack) => {
        logger.error('React Error Boundary caught error:', error);
      }}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// Register service worker for PWA functionality
serviceWorkerRegistration.register({
  onSuccess: () => {
    if(process.env.NODE_ENV==='development')logger.log('PWA: Content cached for offline use');
  },
  onUpdate: (registration) => {
    if(process.env.NODE_ENV==='development')logger.log('🔄 PWA: New content available, please refresh');
    // Optionally show a toast/notification to the user
  },
});

// Check if app can be installed
serviceWorkerRegistration.checkInstallable();
